/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store } from './store';

import { setAuthHintCookie } from '@/utils/auth';
import { setTokenFromStorage, setUser } from './auth/login/slice';
import { signOut } from './auth/signin/slice';
import { resetApp } from './actions';
import { LogoutProvider } from '@/components/auth/LogoutProvider';
import { invalidateSession } from '@/utils/session';

const resetLocalSession = (dispatch: (action: any) => void) => {
  invalidateSession();
  setAuthHintCookie(false);
  dispatch(signOut());
  dispatch(resetApp());
};

function AuthBootstrap() {
  const dispatch = useDispatch();
  const bootedRef = React.useRef(false);
  const login = useSelector((s: any) => s.auth?.login);

  React.useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    try {
      const access = localStorage.getItem('accessToken') || '';
      const refresh = localStorage.getItem('refreshToken') || '';
      const rawProfile = localStorage.getItem('userProfile');

      if (access || refresh) {
        dispatch(setTokenFromStorage({ access, refresh } as any));
        setAuthHintCookie(!!access);
      } else {
        setAuthHintCookie(false);
      }

      if (rawProfile && !login?.user) {
        try {
          const parsed = JSON.parse(rawProfile);
          if (parsed && typeof parsed === 'object') {
            dispatch(setUser(parsed));
          }
        } catch {
          localStorage.removeItem('userProfile');
        }
      }
    } catch {
      resetLocalSession(dispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  React.useEffect(() => {
    try {
      setAuthHintCookie(!!login?.token?.access);
    } catch {
    }
  }, [login?.token?.access]);

  React.useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'accessToken' || e.key === 'refreshToken') {
        const access = localStorage.getItem('accessToken') || '';
        const refresh = localStorage.getItem('refreshToken') || '';
        if (access || refresh) {
          dispatch(setTokenFromStorage({ access, refresh } as any));
          setAuthHintCookie(!!access);
        } else {
          resetLocalSession(dispatch);
        }
      }
      if (e.key === 'userProfile') {
        const raw = localStorage.getItem('userProfile');
        if (raw) {
          try {
            dispatch(setUser(JSON.parse(raw)));
          } catch {
            localStorage.removeItem('userProfile');
          }
        } else {
          dispatch(setUser(null as any));
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [dispatch]);

  return null;
}

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <Provider store={store}>
      <AuthBootstrap />
      <LogoutProvider>{children}</LogoutProvider>
    </Provider>
  );
}
